import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Gratis-Beigabe (z. B. 14-Day-Plan als 0-$-Produkt) zum Paket aus nvp-variant-cards.
 *
 * Add: Nach einem erfolgreichen Add-to-cart aus der Kaufbox legt das Modul das Gift-Produkt
 * dazu, wenn die gewaehlte Karte data-nv-gift traegt. Die Gift-Zeile merkt sich das Paket
 * in der Eigenschaft _nvp_gift_for.
 * Cleanup: Bei jeder Warenkorb-Aenderung fliegt eine Gift-Zeile raus, deren Paket nicht mehr
 * im Warenkorb liegt, und eine doppelte Gift-Zeile wird auf 1 gesetzt.
 *
 * Wird global geladen (layout/theme.liquid), damit der Cleanup auch im Drawer greift.
 */

const SOURCE = 'nvp-gift';
const PARENT_KEY = '_nvp_gift_for';

let busy = false;

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

async function getCart() {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    headers: { Accept: 'application/json' },
  });
  return response.json();
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} body
 */
async function cartPost(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      ...body,
      sections: cartSectionIds().join(','),
      sections_url: window.location.pathname,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || `Cart request failed (${response.status})`);
  }
  return data;
}

function itemCountFromSections(sections) {
  for (const html of Object.values(sections || {})) {
    if (!html) continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector('[ref="cartItemCount"]');
    if (node) return parseInt(node.textContent || '0', 10) || 0;
  }
  return 0;
}

function publish(data) {
  const sections = data.sections || {};
  document.dispatchEvent(
    new CartUpdateEvent(data, SOURCE, {
      source: SOURCE,
      itemCount: itemCountFromSections(sections),
      sections,
    })
  );
}

/** Gift-Variante und Paket-Variante, wenn in der Kaufbox gerade die Gift-Karte gewaehlt ist. */
function selectedGift(productId) {
  const boxes = Array.from(document.querySelectorAll('nv-purchase-tabs[data-nv-gift-variant]'));
  const box = boxes.find((el) => !productId || el.dataset.nvProductId === String(productId));
  if (!(box instanceof HTMLElement)) return null;

  const checked = box.querySelector('[data-nv-card] input[type="radio"]:checked');
  const card = checked?.closest('[data-nv-card]');
  if (!card || !card.hasAttribute('data-nv-gift')) return null;

  const parent = checked instanceof HTMLElement ? checked.dataset.variantId : '';
  const gift = box.dataset.nvGiftVariant;
  if (!parent || !gift) return null;
  return { gift: Number(gift), parent: String(parent) };
}

async function addGift(productId) {
  const selection = selectedGift(productId);
  if (!selection) return;

  const cart = await getCart();
  if (cart.items.some((item) => item.variant_id === selection.gift)) return;

  publish(
    await cartPost('/cart/add.js', {
      items: [{ id: selection.gift, quantity: 1, properties: { [PARENT_KEY]: selection.parent } }],
    })
  );
}

async function cleanup() {
  const cart = await getCart();
  const variantsInCart = new Set(
    cart.items.filter((item) => !item.properties?.[PARENT_KEY]).map((item) => String(item.variant_id))
  );

  /** @type {Record<string, number>} */
  const updates = {};
  const seen = new Set();
  for (const item of cart.items) {
    const parent = item.properties?.[PARENT_KEY];
    if (!parent) continue;
    if (!variantsInCart.has(String(parent)) || seen.has(item.variant_id)) {
      updates[item.key] = 0;
    } else if (item.quantity > 1) {
      updates[item.key] = 1;
    }
    seen.add(item.variant_id);
  }
  if (!Object.keys(updates).length) return;

  publish(await cartPost('/cart/update.js', { updates }));
}

document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  const data = /** @type {any} */ (event).detail?.data || {};
  if (data.source === SOURCE || busy) return;

  busy = true;
  try {
    if (data.source === 'product-form-component' && !data.didError) {
      await addGift(data.productId);
    }
    await cleanup();
  } catch (error) {
    console.warn('[nvp-gift]', error);
  } finally {
    busy = false;
  }
});
